import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import LayoutRightIcon from "@redotech/redo-icons/layout-right.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import type { RedoHorizontalTab } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { RedoHorizontalTabs } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, type ReactNode } from "react";
import { useLocalStorage } from "usehooks-ts";
import { useCustomerOrders } from "../hooks/use-customer-orders";
import { useCustomerReturns } from "../hooks/use-customer-returns";
import { AiInsightsSection } from "./ai-insights-section";
import { AssigneeSelector } from "./assignee-selector";
import { ChannelBadge } from "./badges/support-ticket-badges";
import { CollapsibleSection } from "./collapsible-section";
import { CustomerInfoSection } from "./customer-info-section";
import { FieldLabel } from "./field-label";
import { OrderCard } from "./order-card";
import { PrioritySelector } from "./priority-selector";
import { ReturnCard } from "./return-card";
import { StatusSelector } from "./status-selector";
import { TagSelector } from "./tag-selector";
import { SupportTicketTimeline } from "./timeline/support-ticket-timeline";

export interface SupportTicketDetailsPanelProps {
  supportTicket: SupportTicketListItemResponse;
  onCollapse?: () => void;
}

/**
 * Right panel component that displays support ticket details.
 * Shows overview (status, priority) and customer info placeholders.
 */
type DetailsPanelTab = "details" | "activity";

const DETAILS_PANEL_TABS: RedoHorizontalTab<DetailsPanelTab>[] = [
  { label: "Details", key: "details" },
  { label: "Activity", key: "activity" },
];

export const SupportTicketDetailsPanel = memo(
  function SupportTicketDetailsPanel({
    supportTicket,
    onCollapse,
  }: SupportTicketDetailsPanelProps) {
    const [selectedTab, setSelectedTab] = useLocalStorage<DetailsPanelTab>(
      "redo.support-v2.support-ticket-details.selected-tab",
      "details",
    );

    function renderTab(tab: DetailsPanelTab): ReactNode {
      switch (tab) {
        case "details":
          return <DetailsTab supportTicket={supportTicket} />;
        case "activity":
          return <SupportTicketTimeline supportTicketId={supportTicket.id} />;
        default:
          assertNever(tab);
      }
    }

    return (
      <Flex dir="column" flex={1} overflow="hidden">
        <Flex
          align="flex-end"
          borderBottomWidth="1px"
          borderColor="secondary"
          borderStyle="solid"
          shrink={0}
          style={{ height: 48 }}
        >
          <RedoHorizontalTabs
            drawBottomLine={false}
            pl="xl"
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            size="sm"
            tabs={DETAILS_PANEL_TABS}
          />
          {onCollapse && (
            <Flex align="center" pr="md" style={{ height: "100%" }}>
              <RedoButton
                hierarchy="tertiary"
                IconLeading={LayoutRightIcon}
                onClick={onCollapse}
                size="sm"
              />
            </Flex>
          )}
        </Flex>
        {renderTab(selectedTab)}
      </Flex>
    );
  },
);

interface DetailsTabProps {
  supportTicket: SupportTicketListItemResponse;
}

const DetailsTab = memo(function DetailsTab({
  supportTicket,
}: DetailsTabProps) {
  const customerEmail = supportTicket.customer?.email;
  const ordersResult = useCustomerOrders(customerEmail);
  const returnsResult = useCustomerReturns(customerEmail);

  return (
    <Flex
      dir="column"
      flex={1}
      gap="none"
      overflowY="auto"
      pb="md"
      px="xl"
      style={{ minHeight: 0 }}
    >
      <SupportTicketOverviewSection supportTicket={supportTicket} />
      <AiInsightsSection supportTicket={supportTicket} />
      <CustomerInfoSection
        orders={ordersResult.orders}
        ordersPending={ordersResult.pending}
        supportTicket={supportTicket}
      />
      <OrdersSection
        error={ordersResult.error}
        orders={ordersResult.orders}
        pending={ordersResult.pending}
      />
      <ReturnsSection
        error={returnsResult.error}
        pending={returnsResult.pending}
        returns={returnsResult.returns}
      />
    </Flex>
  );
});

interface SupportTicketOverviewSectionProps {
  supportTicket: SupportTicketListItemResponse;
}

const SupportTicketOverviewSection = memo(
  function SupportTicketOverviewSection({
    supportTicket,
  }: SupportTicketOverviewSectionProps) {
    const lastResponseAt = new Date(
      Math.max(
        supportTicket.lastMerchantMessageAt?.getTime() ?? -1,
        supportTicket.lastCustomerMessageAt?.getTime() ?? -1,
      ),
    );

    return (
      <CollapsibleSection
        localStorageKey="redo.support-v2.support-ticket-details.ticket.collapsed"
        title="Ticket"
      >
        <AssigneeSelector
          mongoAssigneeId={supportTicket.mongoAssigneeId}
          supportTicketId={supportTicket.id}
        />
        <PrioritySelector
          priority={supportTicket.priority}
          supportTicketId={supportTicket.id}
        />
        <StatusSelector
          status={supportTicket.status}
          supportTicketId={supportTicket.id}
        />
        <Flex align="center" gap="md" justify="space-between">
          <FieldLabel>Channel</FieldLabel>
          {supportTicket.channels.map((channel) => (
            <ChannelBadge channel={channel} key={channel} size="sm" />
          ))}
        </Flex>
        <Flex align="center" gap="md" justify="space-between">
          <FieldLabel>Created at</FieldLabel>
          <Text fontSize="xs" textColor="primary">
            {supportTicket.createdAt
              ? supportTicket.createdAt.toLocaleString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                })
              : "-"}
          </Text>
        </Flex>
        <Flex align="center" gap="md" justify="space-between">
          <FieldLabel>Last response</FieldLabel>
          <Text fontSize="xs" textColor="primary">
            {lastResponseAt.toLocaleString(undefined, {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            })}
          </Text>
        </Flex>
        <TagSelector
          currentTagIds={supportTicket.tagIds}
          supportTicketId={supportTicket.id}
        />
      </CollapsibleSection>
    );
  },
);

interface OrdersSectionProps {
  orders: ReturnType<typeof useCustomerOrders>["orders"];
  pending: boolean;
  error: Error | undefined;
}

const OrdersSection = memo(function OrdersSection({
  orders,
  pending,
  error,
}: OrdersSectionProps) {
  if (pending || (!error && orders.length === 0)) {
    return null;
  }

  return (
    <CollapsibleSection
      localStorageKey="redo.support-v2.support-ticket-details.orders.collapsed"
      title="Orders"
    >
      {error ? (
        <Text fontSize="xs" textColor="error">
          Failed to load orders
        </Text>
      ) : (
        <Flex dir="column" gap="md">
          {orders.map((order) => (
            <OrderCard key={order._id} order={order} />
          ))}
        </Flex>
      )}
    </CollapsibleSection>
  );
});

interface ReturnsSectionProps {
  returns: ReturnType<typeof useCustomerReturns>["returns"];
  pending: boolean;
  error: Error | undefined;
}

const ReturnsSection = memo(function ReturnsSection({
  returns,
  pending,
  error,
}: ReturnsSectionProps) {
  if (pending || (!error && returns.length === 0)) {
    return null;
  }

  return (
    <CollapsibleSection
      localStorageKey="redo.support-v2.support-ticket-details.returns.collapsed"
      title="Returns"
    >
      {error ? (
        <Text fontSize="xs" textColor="error">
          Failed to load returns
        </Text>
      ) : (
        <Flex dir="column" gap="md">
          {returns.map((returnData) => (
            <ReturnCard key={returnData._id} return={returnData} />
          ))}
        </Flex>
      )}
    </CollapsibleSection>
  );
});
